import { toast } from 'react-toastify';
import { createId } from '@paralleldrive/cuid2';
import { isSome } from 'fp-ts/es6/Option';
import ServiceContainer from 'src/ServiceContainer';
import { processApiParams, getUrl } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.utils';
import { extractNavPathFromString } from 'src/pages/NavigationShell/navigationUtils';
import type { AddBreadcrumbPayload, CrumbPath } from 'src/state/breadcrumbs/Bread.slice';
import { ClientDataApi } from 'configuration-schemas/confdefnView';
import { isNil, reduce } from 'lodash';
import type { NavigateFunction } from 'react-router';

export type RunLinkActionArgs = {
  dataApi: ClientDataApi;
  selectedItems: unknown[]; // keep generic so this works for grid rows + cards
  breadcrumb?: string;
  routerNavigate?: NavigateFunction;
  addCrumb?: (payload: AddBreadcrumbPayload) => void;
};

export function runLinkAction({
  dataApi,
  selectedItems,
  breadcrumb,
  routerNavigate,
  addCrumb,
}: RunLinkActionArgs): string | undefined {
  try {
    if (!selectedItems || selectedItems.length === 0) {
      return;
    }

    const allParams = selectedItems
      .map((s) => {
        return processApiParams(dataApi, s as any).params;
      })
      .filter((i) => !isNil(i)) as unknown as Record<string, string>; // type things, this works better in ts 5.9.x

    // going to merge the `params` from all the selected items onto the first, so that arrays are
    const firstProcessedDataApi = processApiParams(dataApi, selectedItems[0] as any);
    firstProcessedDataApi.params = reduce(
      allParams,
      (merged, current) => {
        Object.keys(current).forEach((key) => {
          if (merged && merged[key]) {
            merged[key] = (merged[key] as any).concat((current as any)[key]);
          } else {
            merged[key] = (current as any)[key];
          }
        });
        return merged;
      },
      {} as any,
    );

    const url = getUrl(firstProcessedDataApi);

    if (breadcrumb) {
      // remove the query param from the crumb for path processing
      const currentLocation = extractNavPathFromString(window.location.hash.split('?')[0]);
      const clqueryParams = window.location.hash.split('?')[1];
      // remove the query params from the crumb for path processing
      const newLocation = extractNavPathFromString(url.split('?')[0]);
      const nlqueryParams = url.split('?')[1];

      if (isSome(currentLocation) && isSome(newLocation)) {
        const currentPathStr = createId();
        const currentUrl = window.location.hash.replace('#', '');
        const currentCrumbPath: CrumbPath = {
          crumbId: currentPathStr,
          path: currentLocation.value,
          queryParams: clqueryParams,
          url: currentUrl,
          // no restore, as the user is selecting multiple items
          restore: {
            selectedCompanionId: null,
            selectedGridRowId: null,
            selectedGridColumnId: null,
            selectedLevel: null,
          },
        };
        const newPathStr = createId();
        const nextCrumbPath: CrumbPath = {
          crumbId: newPathStr,
          path: newLocation.value,
          queryParams: nlqueryParams,
          url,
          restore: {
            // not necessary to set a value until they become the currentCrumbPath
            selectedCompanionId: null,
            selectedGridRowId: null,
            selectedGridColumnId: null,
            selectedLevel: null,
          },
        };

        addCrumb?.({
          breadcrumbKey: breadcrumb,
          currentCrumbPath,
          nextCrumbPath,
        });
      }
    }

    if (routerNavigate) {
      routerNavigate(url);
    }

    return url;
  } catch (e) {
    const err = e as Error;
    toast.error('A failure occured performing the action');
    ServiceContainer.loggingService.error('Failed To link via button', err.stack);
    return;
  }
}
